<template>
  <div v-if="!isLoading" :class="$style.shop_top">
    <div :class="$style.heading_area">
      <MoleculesConsumerHeading :class="$style.heading" title="Shop List" sub-title="店舗一覧" align="left" />
    </div>

    <div v-if="officialStores.length" :class="$style.official_store_container">
      <h2 :class="$style.text_box">TENTIAL OFFICIAL STORE</h2>
      <p :class="$style.official_store_link">
        <AtomsConsumerLink url="/shop/official"> オフィシャルストア一覧はこちら </AtomsConsumerLink>
      </p>
      <div :class="$style.official_store_list">
        <StoreCarousel :is-md="isMd" :parallel-list="officialStores" />
      </div>
    </div>

    <div :class="$style.list_container">
      <div v-if="!isMd" ref="filterRootRef" :class="$style.filter_root">
        <div :class="$style.quick_filter">
          <div :class="$style.filter_count">全 {{ allCount }} 店舗中 {{ listCount }}店舗 を表示</div>
          <AtomsConsumerButton :class="$style.filter_button" @click="isOpenFilterSp = true">
            <AtomsConsumerImage src="/images/icon/filter-icon.svg" alt="filter" :width="16" :height="16" />
            絞り込み
          </AtomsConsumerButton>
        </div>
      </div>
      <div ref="filterRef" :class="$style.filter_area">
        <StoreFilter
          ref="filterComponentRef"
          :list-count="listCount"
          :is-md="isMd"
          :is-open-filter-sp="isOpenFilterSp"
          :location-error-message="locationErrorMessageRef"
          :update-location-error="updateLocationError"
          :update-is-loading="updateIsLoading"
          :scroll-to-filter="scrollToFilter"
          @update:is-open-filter-sp="isOpenFilterSp = $event"
        />
      </div>
      <div ref="storeTableList" :class="$style.list_area">
        <StoreTableList
          :stores="storeList"
          :current-page="page"
          :total-page-count="pageCount"
          :all-count="allCount"
          :list-count="listCount"
          :is-md="isMd"
        />
      </div>
    </div>

    <div :class="$style.store_tail">
      <div :class="$style.store_desc">
        <p>※お取り扱いアイテムは店舗によって異なります</p>
        <p>※取扱い商品についてのお問い合わせは直接店舗にご連絡ください。</p>
        <p>
          ※予告なしにお取り扱いがなくなる場合もございますので、在庫状況は店舗に直接ご確認いただいてからご来店いただくことをお薦めします。
        </p>
      </div>
    </div>
  </div>
  <OrganismsLoading v-else />
</template>

<script setup lang="ts">
import { officialStore, dealerStore } from "@tential/ec-gql-schema/master/store"
import { createItemListJsonLD, createStoreJsonLD, useCustomMeta, createBreadcrumbJsonLD } from "~/composables/meta"
import { PublicStoreSearchQueryVariables, StoreSearchFilter } from "~/types/type.generated"
import { usePublicStoreAllQuery, usePublicStoreSearchQuery } from "~/gql/urql.generated"
const locationErrorMessageRef = ref<string>("")
const isOpenFilterSp = ref(false)
const isLoading = ref<boolean>(false)
const { y } = useWindowScroll()
const { width: windowWidth } = useWindowSize()
const route = useRoute()
const customMeta = useCustomMeta()
const storeTableList = ref<HTMLElement>()
const filterRef = ref<HTMLElement>()
const filterRootRef = ref<HTMLElement>()
const filterComponentRef = ref<HTMLElement>()
const perPage = 10
const page = computed(() => {
  return typeof route.query.page === "string" ? +route.query.page : 1
})
const isMd = computed(() => windowWidth.value >= 768)

const { data: usePublicStoreAllOfficialStoreQueryRet, executeQuery: usePublicStoreAllQueryOfficialStoreExec } =
  usePublicStoreAllQuery({
    variables: {
      filter: {
        _operators: {
          type: {
            in: ["official", "popup"],
          },
        },
        type: "official",
        is_status: true,
      },
      sort: "DISPLAY_ORDER_NUMBER_ASC",
    },
    requestPolicy: "network-only",
    pause: true,
  })

const { data: officialStoreRes } = await useAsyncData("usePublicStoreAllQueryOfficialStore", async () => {
  await usePublicStoreAllQueryOfficialStoreExec()
  return usePublicStoreAllOfficialStoreQueryRet.value?.publicStoreAll
})

const officialStores = computed(() => {
  return officialStoreRes.value || []
})

const setFilterPosition = (): void => {
  const headerElement = document.getElementById("site-header-id")
  if (!headerElement) return
  const headerHeight = headerElement.clientHeight
  if (isMd.value) {
    const filterElement = filterRef.value
    if (!filterElement) return
    headerElement.classList.contains("hide")
      ? (filterElement.style.top = "40px")
      : (filterElement.style.top = headerHeight + 40 + "px")
    return
  }
  const filterElement = filterRootRef.value
  if (!filterElement) return
  headerElement.classList.contains("hide")
    ? (filterElement.style.top = "0")
    : (filterElement.style.top = headerHeight + "px")
}

const setFilterType = (query: string): string[] => {
  const result = []
  if (query.includes("official")) result.push(...officialStore)
  if (query.includes("dealer")) result.push(...dealerStore)
  return result
}

const csvToArray = (queryValue: string) => queryValue?.split(",").filter((v) => v !== "") ?? []
const createVariables = (): PublicStoreSearchQueryVariables => {
  const filter: StoreSearchFilter = {}
  filter.prefecture = typeof route.query.prefecture === "string" ? route.query.prefecture : undefined
  filter.prefectures = Array.isArray(route.query.prefecture)
    ? route.query.prefecture.filter((p): p is string => p !== null)
    : undefined
  filter.products = typeof route.query.products === "string" ? csvToArray(route.query.products) : undefined
  filter.type = typeof route.query.type === "string" ? setFilterType(route.query.type) : undefined
  filter.scale = typeof route.query.scale === "string" ? csvToArray(route.query.scale) : undefined
  filter.others_select = typeof route.query.otherSelect === "string" ? csvToArray(route.query.otherSelect) : undefined
  filter.longitude = typeof route.query.longitude === "string" ? parseFloat(route.query.longitude) : undefined
  filter.latitude = typeof route.query.latitude === "string" ? parseFloat(route.query.latitude) : undefined
  filter.searchText = typeof route.query.searchText === "string" ? route.query.searchText : undefined

  return {
    page: page.value,
    perPage,
    filter,
  }
}

const storeSearchVariables = ref<PublicStoreSearchQueryVariables>(createVariables())
const { data: usePublicStoreSearchQueryRet, executeQuery: usePublicStoreSearchQueryExec } = usePublicStoreSearchQuery({
  variables: storeSearchVariables,
  requestPolicy: "network-only",
  pause: true,
})

const { data: storeListRes } = await useAsyncData(async () => {
  isLoading.value = true
  await usePublicStoreSearchQueryExec()
  isLoading.value = false
  return { data: usePublicStoreSearchQueryRet.value }
})
if (!usePublicStoreSearchQueryRet.value) usePublicStoreSearchQueryRet.value = storeListRes.value?.data

const storeList = computed(() => usePublicStoreSearchQueryRet.value?.publicStoreSearch?.items || [])
const allCount = computed(() => usePublicStoreSearchQueryRet.value?.publicStoreSearch?.allCount || 0)
const listCount = computed(() => usePublicStoreSearchQueryRet.value?.publicStoreSearch?.count || 0)
const pageCount = computed(() => usePublicStoreSearchQueryRet.value?.publicStoreSearch?.pageInfo?.pageCount || 1)

const pageReExec = async () => {
  storeSearchVariables.value = createVariables()
  await nextTick(usePublicStoreSearchQueryExec)
}

const updateLocationError = (errorMessage: string) => {
  locationErrorMessageRef.value = errorMessage
}

const updateIsLoading = (value: boolean) => {
  isLoading.value = value
}

const scrollToFilter = () => {
  nextTick(() => {
    const element = filterRef.value
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
      })
    }
  })
}

watch(
  () => route.fullPath,
  async () => {
    await pageReExec()
  },
)

watch(y, () => {
  setFilterPosition()
})

useHead(
  customMeta.fetch({
    title: "TENTIAL取扱店・販売店一覧ページ",
    description: "TENTIAL[テンシャル]の取扱店・販売店一覧ページです。お気軽に、ご連絡ください。",
    jsonLDInfo: [
      createItemListJsonLD({
        count: storeList.value?.length,
        items: storeList.value?.map((store) => {
          return createStoreJsonLD(store)
        }),
      }),
      createBreadcrumbJsonLD([
        {
          title: "TENTIAL取扱店・販売店一覧ページ",
          url: route.path,
        },
      ]),
    ],
  }),
)
</script>

<style lang="scss" scoped module>
.shop_top {
  @include container-lg;
  .heading_area {
    @include md {
      padding: 0;
    }
  }
  .location_button_area {
    margin-bottom: 2rem;
    @include md {
      width: 30%;
    }
  }
  .select_area {
    display: flex;
    flex-flow: column;
    gap: 8px 0;
    text-align: center;
    width: 100%;
    margin: 40px auto 60px;
    @include md {
      padding: 0 274px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      text-align: left;
      margin: 90px 0 120px;
      gap: 23px 0;
    }
    .single_area {
      .area_line {
        .button {
          color: $primary;
          font-weight: normal;
          font-size: $font-size-14;
          line-height: 1.43;
          @include md {
            font-size: $font-size-16;
          }
        }
        .oc_svg {
          display: none;
          &.show {
            display: inline-block;
            @include md {
              display: none;
            }
          }
          @include md {
            width: 11px;
            height: 11px;
            img {
              vertical-align: unset;
            }
          }
        }
      }
      .prefecture_line {
        line-height: 1.5;
        margin-top: 0;
        display: none;
        @include md {
          display: block;
          margin-top: 8px;
        }
        &.show {
          display: block;
        }
        .button {
          display: inline-block;
          padding: 0px 0.3rem;
          color: $primary;
          font-size: $font-size-12;
          font-weight: normal;
          line-height: 1;
          border-right: 1px solid #000;
          cursor: pointer;
          @include md {
            font-size: $font-size-14;
          }
          &:first-child {
            padding: 0px 0.5rem;
            @include md {
              padding-left: 0;
            }
          }
          &:last-child {
            border-right: none;
          }
          &.off {
            color: $black-30;
            cursor: default;
          }
        }
      }
    }
  }
  .official_store_container {
    margin-bottom: 5rem;
    .text_box {
      font-size: $font-size-16;
      line-height: 2;
      letter-spacing: 0.32px;
      color: $primary;
      font-weight: bold;
      @include md {
        font-size: $font-size-22;
        line-height: 33px;
      }
    }
    .official_store_list {
      margin-bottom: 2rem;
    }
    .official_store_link {
      margin-bottom: 1rem;
      a {
        font-size: $font-size-14;
        color: $primary;
        text-decoration: underline;
      }
    }
  }
  .list_container {
    display: flex;
    flex-flow: column;
    position: relative;
    @include md {
      flex-flow: row;
    }
    .filter_root {
      position: sticky;
      top: 0;
      z-index: 3;
      transition: 0.4s ease-in-out;
      background: rgba($white, 0.9);
      backdrop-filter: blur(10px);
      border-top: 1px solid $primary-20;
      border-bottom: 1px solid $primary-20;
      margin-bottom: 2rem;
      .filter_count {
        margin-right: auto;
      }
      .filter_button {
        @include sub-font;
        text-decoration: underline;
      }
      .quick_filter {
        display: flex;
        align-items: center;
        justify-content: right;
        gap: 1rem;
        padding: 1rem 0.5rem;
        white-space: nowrap;
        @include md {
          justify-content: left;
        }
      }
    }
    .filter_area {
      width: 100%;
      border-bottom: 1px solid $primary-20;
      margin-bottom: 1rem;
      @include md {
        position: sticky;
        top: 0;
        z-index: 3;
        transition: 0.4s ease-in-out;
        width: 30%;
        padding-right: 3rem;
        border: none;
        max-height: 80vh;
        overflow-y: auto;
      }
    }
    .list_area {
      width: 100%;
      @include md {
        width: 70%;
      }
    }
  }
  .shop_popup_store,
  .shop_agency_store {
    margin-top: 78px;
    @include md {
      margin-top: 44px;
    }
  }
  .shop_prefecture_store {
    .text_box {
      text-align: center;
      display: flex;
      flex-direction: column;
      gap: 8px;
      span {
        font-size: $font-size-14;
        line-height: 15px;
        @include md {
          font-size: $font-size-16;
          line-height: 22px;
        }
      }
      .button_area {
        line-height: 1;
        .button {
          display: inline-block;
          cursor: pointer;
          font-weight: normal;
          font-size: $font-size-14;
          line-height: 15px;
        }
      }
    }
  }
  .store_tail {
    font-size: $font-size-10;
    margin-top: 40px;
    @include md {
      margin-top: 50px;
      font-size: $font-size-12;
      line-height: 2.16;
    }
    .store_see {
      margin-top: 78px;
      font-size: $font-size-15;
      text-align: center;
      cursor: pointer;
      @include md {
        margin-top: 106px;
      }
    }
  }
}
</style>
